import apiClient, { getApiErrorMessage } from './apiClient';

const unwrapApiData = (data) => data?.data || data?.Data || data;
const unwrapApiList = (data) => {
  const unwrapped = unwrapApiData(data);
  if (Array.isArray(unwrapped)) return unwrapped;
  if (Array.isArray(unwrapped?.items)) return unwrapped.items;
  if (Array.isArray(unwrapped?.Items)) return unwrapped.Items;
  return [];
};

export const normalizeAssistantSettings = (item) => ({
  memoryEnabled: Boolean(item?.MemoryEnabled ?? item?.memoryEnabled),
  personality: item?.Personality || item?.personality || 'default',
  customInstructions: item?.CustomInstructions || item?.customInstructions || '',
  toneTags: item?.ToneTags || item?.toneTags || [],
  preferredName: item?.PreferredName || item?.preferredName || '',
  job: item?.Job || item?.job || '',
  interests: item?.Interests || item?.interests || '',
  memorySummary: item?.MemorySummary || item?.memorySummary || '',
  memorySummaryUpdatedAt: item?.MemorySummaryUpdatedAt || item?.memorySummaryUpdatedAt || null,
  createdAt: item?.CreatedAt || item?.createdAt || null,
  updatedAt: item?.UpdatedAt || item?.updatedAt || null
});

export const normalizeUserMemory = (item) => ({
  id: item?.Id || item?.id,
  content: item?.Content || item?.content || '',
  type: item?.Type || item?.type || 'fact',
  isActive: Boolean(item?.IsActive ?? item?.isActive ?? true),
  source: item?.Source || item?.source || 'extracted',
  importance: Number(item?.Importance ?? item?.importance ?? 0),
  createdAt: item?.CreatedAt || item?.createdAt || null,
  updatedAt: item?.UpdatedAt || item?.updatedAt || null
});

export const fetchMemorySettingsApi = async () => {
  try {
    const { data } = await apiClient.get('/memory/settings');
    return normalizeAssistantSettings(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load memory settings.'));
  }
};

export const updateMemorySettingsApi = async (patch) => {
  try {
    const payload = {};
    if (patch.memoryEnabled !== undefined) payload.MemoryEnabled = patch.memoryEnabled;
    if (patch.personality !== undefined) payload.Personality = patch.personality;
    if (patch.customInstructions !== undefined) payload.CustomInstructions = patch.customInstructions;
    if (patch.toneTags !== undefined) payload.ToneTags = patch.toneTags;
    if (patch.preferredName !== undefined) payload.PreferredName = patch.preferredName;
    if (patch.job !== undefined) payload.Job = patch.job;
    if (patch.interests !== undefined) payload.Interests = patch.interests;
    if (patch.memorySummary !== undefined) payload.MemorySummary = patch.memorySummary;
    const { data } = await apiClient.patch('/memory/settings', payload);
    return normalizeAssistantSettings(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to update memory settings.'));
  }
};

export const fetchMemoriesApi = async () => {
  try {
    const { data } = await apiClient.get('/memory');
    return unwrapApiList(data).map(normalizeUserMemory).filter((item) => item.id);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load memories.'));
  }
};

export const createMemoryApi = async ({ content, type } = {}) => {
  try {
    const { data } = await apiClient.post('/memory', { Content: content, Type: type });
    return normalizeUserMemory(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to create memory.'));
  }
};

export const updateMemoryApi = async (id, { content, type, isActive } = {}) => {
  try {
    const payload = {};
    if (content !== undefined) payload.Content = content;
    if (type !== undefined) payload.Type = type;
    if (isActive !== undefined) payload.IsActive = isActive;
    const { data } = await apiClient.patch(`/memory/${encodeURIComponent(id)}`, payload);
    return normalizeUserMemory(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to update memory.'));
  }
};

export const deleteMemoryApi = async (id) => {
  try {
    await apiClient.delete(`/memory/${encodeURIComponent(id)}`);
    return id;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to delete memory.'));
  }
};

export const deleteAllMemoriesApi = async ({ disable = false } = {}) => {
  try {
    const { data } = await apiClient.delete('/memory', { params: disable ? { disable: true } : undefined });
    const unwrapped = unwrapApiData(data);
    return Number(unwrapped?.DeletedCount ?? unwrapped?.deletedCount ?? 0);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to delete memories.'));
  }
};

export const refreshMemorySummaryApi = async () => {
  try {
    const { data } = await apiClient.post('/memory/summary/refresh');
    const unwrapped = unwrapApiData(data);
    return {
      memorySummary: unwrapped?.MemorySummary || unwrapped?.memorySummary || '',
      memorySummaryUpdatedAt: unwrapped?.MemorySummaryUpdatedAt || unwrapped?.memorySummaryUpdatedAt || new Date().toISOString()
    };
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to refresh memory summary.'));
  }
};

export const logoutAllDevicesApi = async () => {
  try {
    await apiClient.post('/Auth/logout-all');
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to log out of all devices.'));
  }
};

export const deleteAccountApi = async () => {
  try {
    await apiClient.delete('/Auth/me');
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to delete account.'));
  }
};

export const deleteAllConversationsApi = async () => {
  try {
    const { data } = await apiClient.delete('/conversations');
    return Number(unwrapApiData(data) ?? data ?? 0);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to delete conversations.'));
  }
};
